import { useMemo, useState } from 'react';
import { Box, Popover } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  availableDimensions,
  dimensionById,
  enumerateValues,
  type DimensionDef,
  type DimensionId,
} from './dimensions';
import { CategoricalValuePicker } from './CategoricalValuePicker';
import { RangeValuePicker } from './RangeValuePicker';
import { suggestFilters, type FilterSuggestion } from './filterSuggestions';
import { applyFilters, type CategoricalChip, type FilterChip, type RangeChip } from './filters';
import { filterRows, type RosterDomainKey, type RosterRow } from './rosterRows';
import { ROSTER } from './tokens';

const STUDENT_SUGGESTION_LIMIT = 3;

type Props = {
  anchorEl: HTMLElement | null;
  open: boolean;
  admin?: boolean;
  rows: RosterRow[];
  chips: FilterChip[];
  editing?: FilterChip | null;
  onClose: () => void;
  onPickDimension: (id: DimensionId) => void;
  onPickSuggestion: (suggestion: FilterSuggestion) => void;
  onPickStudent: (id: string) => void;
  onApply: (chip: FilterChip) => void;
};

function groupInOrder(dimensions: DimensionDef[]): [string, DimensionDef[]][] {
  const groups: [string, DimensionDef[]][] = [];
  for (const dimension of dimensions) {
    const existing = groups.find(([name]) => name === dimension.group);
    if (existing) existing[1].push(dimension);
    else groups.push([dimension.group, [dimension]]);
  }
  return groups;
}

const DOMAIN_OPTIONS = [
  { value: 'listening', label: 'Listening' },
  { value: 'speaking', label: 'Speaking' },
  { value: 'reading', label: 'Reading' },
  { value: 'writing', label: 'Writing' },
] as const;

function existingValues(chips: FilterChip[], dimension: DimensionId): string[] {
  const match = chips.find((chip) => chip.dimension === dimension && 'values' in chip);
  return match != null && 'values' in match ? match.values : [];
}

function PickerBody({
  rows,
  chips,
  admin,
  editing,
  onPickDimension,
  onPickSuggestion,
  onPickStudent,
  onApply,
  onClose,
}: {
  rows: RosterRow[];
  chips: FilterChip[];
  admin: boolean;
  editing?: FilterChip | null;
  onPickDimension: (id: DimensionId) => void;
  onPickSuggestion: (suggestion: FilterSuggestion) => void;
  onPickStudent: (id: string) => void;
  onApply: (chip: FilterChip) => void;
  onClose: () => void;
}) {
  const [chosen, setChosen] = useState<DimensionId | null>(editing?.dimension ?? null);
  const definition = chosen == null ? undefined : dimensionById(chosen);

  const pick = (id: DimensionId) => {
    onPickDimension(id);
    setChosen(id);
  };

  if (chosen != null && definition?.kind === 'categorical') {
    return (
      <CategoricalValuePicker
        label={definition.label}
        options={enumerateValues(chosen, rows)}
        selected={existingValues(chips, chosen)}
        onApply={(values) =>
          onApply({ dimension: chosen as CategoricalChip['dimension'], values })
        }
        onCancel={onClose}
        onBack={() => setChosen(null)}
      />
    );
  }

  if (chosen != null && definition?.kind === 'range') {
    const rangeChipFor = (forDomain?: string) =>
      chips.find(
        (chip) =>
          chip.dimension === chosen &&
          !('values' in chip) &&
          (chip as RangeChip).domain === forDomain,
      ) as RangeChip | undefined;
    const editedDomain =
      editing != null && editing.dimension === chosen && !('values' in editing)
        ? editing.domain
        : undefined;
    const openOnDomain = definition.hasDomainAxis
      ? (editedDomain ?? DOMAIN_OPTIONS[0].value)
      : undefined;
    const existing = rangeChipFor(openOnDomain);
    return (
      <RangeValuePicker
        label={definition.label}
        scale={definition.scale}
        from={existing?.from}
        to={existing?.to}
        domains={definition.hasDomainAxis ? DOMAIN_OPTIONS : undefined}
        domain={openOnDomain}
        boundsFor={
          definition.hasDomainAxis
            ? (forDomain) => ({
                from: rangeChipFor(forDomain)?.from,
                to: rangeChipFor(forDomain)?.to,
              })
            : undefined
        }
        onApply={({ from, to, domain }) =>
          onApply(
            definition.hasDomainAxis
              ? { dimension: 'domainPL', domain: domain as RosterDomainKey, from, to }
              : ({ dimension: chosen, from, to } as RangeChip),
          )
        }
        onCancel={onClose}
        onBack={() => setChosen(null)}
      />
    );
  }

  return (
    <DimensionList
      rows={rows}
      chips={chips}
      admin={admin}
      onPickDimension={pick}
      onPickSuggestion={onPickSuggestion}
      onPickStudent={onPickStudent}
    />
  );
}

function SuggestionGroup({ title, first }: { title: string; first: boolean }) {
  return (
    <Box
      data-testid="suggestion-group"
      sx={{
        p: '11px 14px 3px',
        fontFamily: ROSTER.font,
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: ROSTER.muted,
        borderTop: first ? 0 : `1px solid ${ROSTER.hairline}`,
      }}
    >
      {title}
    </Box>
  );
}

const suggestionRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  p: '8px 14px',
  border: 0,
  background: 'transparent',
  textAlign: 'left',
  fontFamily: ROSTER.font,
  fontSize: 14.5,
  color: ROSTER.ink,
  cursor: 'pointer',
  '&:hover': { bgcolor: ROSTER.hoverBg },
} as const;

function DimensionList({
  rows,
  chips,
  admin,
  onPickDimension,
  onPickSuggestion,
  onPickStudent,
}: {
  rows: RosterRow[];
  chips: FilterChip[];
  admin: boolean;
  onPickDimension: (id: DimensionId) => void;
  onPickSuggestion: (suggestion: FilterSuggestion) => void;
  onPickStudent: (id: string) => void;
}) {
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = availableDimensions({ admin }).filter(
      (dimension) => q === '' || dimension.label.toLowerCase().includes(q),
    );
    return groupInOrder(matches);
  }, [query, admin]);

  const suggestions = useMemo(
    () => suggestFilters(query, rows, chips, undefined, admin),
    [query, rows, chips, admin],
  );

  const students = useMemo(
    () =>
      query.trim() === ''
        ? []
        : filterRows(applyFilters(rows, chips), query).slice(0, STUDENT_SUGGESTION_LIMIT),
    [query, rows, chips],
  );

  const nothingMatched =
    groups.length === 0 && suggestions.length === 0 && students.length === 0;

  return (
    <Box data-testid="dimension-picker">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          p: '11px 14px',
          borderBottom: `1px solid ${ROSTER.hairline}`,
        }}
      >
        <SearchIcon sx={{ width: 16, height: 16, color: ROSTER.muted }} />
        <Box
          component="input"
          data-testid="dimension-picker-search"
          aria-label="Search filters or students"
          autoFocus
          placeholder="Search filters or students…"
          value={query}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          sx={{
            border: 0,
            width: '100%',
            '&:focus-visible': {
              outline: `2px solid ${ROSTER.ink}`,
              outlineOffset: '2px',
              borderRadius: '2px',
            },
            fontFamily: ROSTER.font,
            fontSize: 14.5,
            color: ROSTER.ink,
            '&::placeholder': { color: ROSTER.muted },
          }}
        />
      </Box>
      <Box sx={{ overflowY: 'auto' }}>
        {suggestions.length > 0 && <SuggestionGroup title="Add a filter" first />}
        {suggestions.map((suggestion) => (
          <Box
            key={`${suggestion.dimension}:${suggestion.value}`}
            component="button"
            type="button"
            data-testid="filter-suggestion"
            aria-label={`Add filter ${suggestion.dimensionLabel}: ${suggestion.valueLabel}`}
            onClick={() => onPickSuggestion(suggestion)}
            sx={{ ...suggestionRowSx, fontWeight: 600 }}
          >
            {`${suggestion.dimensionLabel}: ${suggestion.valueLabel}`}
          </Box>
        ))}
        {students.length > 0 && (
          <SuggestionGroup title="Jump to a student" first={suggestions.length === 0} />
        )}
        {students.map((student) => (
          <Box
            key={student.id}
            component="button"
            type="button"
            data-testid="student-suggestion"
            aria-label={`Jump to ${student.name}, ${student.school}`}
            onClick={() => onPickStudent(student.id)}
            sx={suggestionRowSx}
          >
            {student.name}
            <Box component="span" sx={{ fontSize: 12.5, color: ROSTER.muted }}>
              {`· ${student.school}`}
            </Box>
          </Box>
        ))}
        {nothingMatched && (
          <Box
            role="status"
            sx={{
              p: '14px',
              fontFamily: ROSTER.font,
              fontSize: 14.5,
              color: ROSTER.muted,
            }}
          >
            No filters match that search.
          </Box>
        )}
        {groups.map(([group, dimensions], index) => (
          <Box key={group}>
            <Box
              data-testid="dimension-group"
              sx={{
                p: '11px 14px 3px',
                fontFamily: ROSTER.font,
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: ROSTER.muted,
                borderTop:
                  index > 0 || suggestions.length > 0 || students.length > 0
                    ? `1px solid ${ROSTER.hairline}`
                    : 0,
                mt: index > 0 ? '2px' : 0,
              }}
            >
              {group}
            </Box>
            {dimensions.map((dimension) => (
              <Box
                key={dimension.id}
                component="button"
                type="button"
                data-testid="dimension-row"
                aria-label={`Filter by ${dimension.label}`}
                onClick={() => onPickDimension(dimension.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  p: '8px 14px',
                  border: 0,
                  background: 'transparent',
                  textAlign: 'left',
                  fontFamily: ROSTER.font,
                  fontSize: 14.5,
                  color: ROSTER.ink,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: ROSTER.hoverBg },
                }}
              >
                {dimension.label}
                <Box component="span" sx={{ color: ROSTER.muted }}>
                  ›
                </Box>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function DimensionPicker({
  anchorEl,
  open,
  admin = false,
  rows,
  chips,
  editing,
  onClose,
  onPickDimension,
  onPickSuggestion,
  onPickStudent,
  onApply,
}: Props) {
  const [openCount, setOpenCount] = useState(0);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setOpenCount((count) => count + 1);
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{
        paper: { sx: { width: 320, maxHeight: '80vh', borderRadius: '12px' } },
      }}
    >
      <PickerBody
        key={openCount}
        rows={rows}
        chips={chips}
        admin={admin}
        editing={editing}
        onPickDimension={onPickDimension}
        onPickSuggestion={onPickSuggestion}
        onPickStudent={onPickStudent}
        onApply={onApply}
        onClose={onClose}
      />
    </Popover>
  );
}
